// source: template-project@7007a338a apps/backoffice/src/components/ui/shadcn/accordion.tsx (look-alike: built on ./collapsible, no Radix)
// NEW local look-alike (plan P4-2): `@radix-ui/react-accordion` is not a dependency of this app and none may be added, so
// this file exports the SAME names and props as TP's shadcn accordion (Accordion / AccordionItem / AccordionTrigger /
// AccordionContent), on top of our local Collapsible:
//   <Accordion type="multiple" defaultValue={['a']}><AccordionItem value="a"><AccordionTrigger>…</AccordionTrigger>
//   <AccordionContent>…</AccordionContent></AccordionItem></Accordion>
//
// Contract (Radix's, the subset TP uses):
//   - Accordion: `type="single"` (value is a string; `collapsible` lets the open item close again, otherwise one item
//     always stays open once opened) or `type="multiple"` (value is a string[]). Controlled (`value` + `onValueChange`)
//     or uncontrolled (`defaultValue`). `disabled` disables every item.
//   - AccordionItem: `value`, `disabled`; a Collapsible with `data-state="open|closed"`.
//   - AccordionTrigger: a <button> inside an <h3>, with `aria-expanded` and `aria-controls` -> the content (Collapsible's
//     wiring). Up / Down / Home / End move focus between the triggers of the same accordion root (not a nested one's).
//   - AccordionContent: the collapsible content, a `role="region"` labelled by its trigger, `hidden` and unmounted while closed. The opening animation reads
//     `--radix-accordion-content-height`, mapped from the Collapsible's measured height.
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface AccordionContextValue {
  isOpen: (value: string) => boolean;
  setItemOpen: (value: string, open: boolean) => void;
  disabled: boolean;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

function useAccordionContext(part: string): AccordionContextValue {
  const ctx = React.useContext(AccordionContext);
  if (!ctx) throw new Error(`${part} must be used within <Accordion>`);
  return ctx;
}

const TRIGGER_ATTR = 'data-accordion-trigger';
const ROOT_ATTR = 'data-accordion-root';

/** Per item: the trigger's id, which the content's `aria-labelledby` points at. */
const AccordionItemContext = React.createContext<{ triggerId: string } | null>(null);

function useAccordionItemContext(part: string): { triggerId: string } {
  const ctx = React.useContext(AccordionItemContext);
  if (!ctx) throw new Error(`${part} must be used within <AccordionItem>`);
  return ctx;
}

/** The accordion root a trigger belongs to: the nearest one, so a nested accordion's triggers are not the outer one's. */
const rootOf = (trigger: Element): Element | null => trigger.closest(`[${ROOT_ATTR}]`);

interface AccordionBaseProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'defaultValue' | 'dir'> {
  disabled?: boolean;
}

export interface AccordionSingleProps extends AccordionBaseProps {
  type: 'single';
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Lets the open item be closed again. Without it, an item that was opened stays open until another one opens. */
  collapsible?: boolean;
}

export interface AccordionMultipleProps extends AccordionBaseProps {
  type: 'multiple';
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

const asList = (value: string | string[] | undefined): string[] => (value === undefined || value === '' ? [] : Array.isArray(value) ? value : [value]);

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(function Accordion(props, ref) {
  const { type, disabled = false, onKeyDown, children, ...rest } = props;
  // The type-specific props must not reach the <div>.
  const {
    value: valueProp,
    defaultValue,
    onValueChange,
    collapsible,
    ...divProps
  } = rest as typeof rest & { value?: string | string[]; defaultValue?: string | string[]; onValueChange?: (value: never) => void; collapsible?: boolean };

  const isControlled = valueProp !== undefined;
  const [uncontrolled, setUncontrolled] = React.useState<string[]>(() => asList(defaultValue));
  const open = isControlled ? asList(valueProp) : uncontrolled;

  const isOpen = React.useCallback((value: string) => open.includes(value), [open]);

  const setItemOpen = React.useCallback(
    (value: string, nextOpen: boolean) => {
      let next: string[];
      if (type === 'multiple') {
        next = nextOpen ? [...open.filter((v) => v !== value), value] : open.filter((v) => v !== value);
      } else if (nextOpen) {
        next = [value];
      } else if (collapsible) {
        next = [];
      } else {
        return;
      }
      if (!isControlled) setUncontrolled(next);
      if (type === 'multiple') (onValueChange as ((v: string[]) => void) | undefined)?.(next);
      else (onValueChange as ((v: string) => void) | undefined)?.(next[0] ?? '');
    },
    [type, open, collapsible, isControlled, onValueChange],
  );

  const ctx = React.useMemo(() => ({ isOpen, setItemOpen, disabled }), [isOpen, setItemOpen, disabled]);

  const onKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const current = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>(`[${TRIGGER_ATTR}]`) : null;
    const root = event.currentTarget;
    if (!current || rootOf(current) !== root) return;
    const triggers = [...root.querySelectorAll<HTMLElement>(`[${TRIGGER_ATTR}]:not([disabled])`)].filter((t) => rootOf(t) === root);
    const index = triggers.indexOf(current);
    if (index < 0) return;
    let target: HTMLElement | undefined;
    if (event.key === 'ArrowDown') target = triggers[(index + 1) % triggers.length];
    else if (event.key === 'ArrowUp') target = triggers[(index - 1 + triggers.length) % triggers.length];
    else if (event.key === 'Home') target = triggers[0];
    else if (event.key === 'End') target = triggers[triggers.length - 1];
    if (target) {
      event.preventDefault();
      target.focus();
    }
  };

  return (
    <AccordionContext.Provider value={ctx}>
      {/* The keys are a shortcut between the triggers (real buttons that are already focusable): the same as Radix's accordion root. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div ref={ref} data-orientation="vertical" {...{ [ROOT_ATTR]: '' }} {...divProps} onKeyDown={onKey}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
});

export interface AccordionItemProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'defaultChecked'> {
  value: string;
  disabled?: boolean;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(function AccordionItem(
  { className, value, disabled = false, ...props },
  ref,
) {
  const ctx = useAccordionContext('AccordionItem');
  const triggerId = React.useId();
  const item = React.useMemo(() => ({ triggerId }), [triggerId]);
  return (
    <AccordionItemContext.Provider value={item}>
      <Collapsible
        ref={ref}
        open={ctx.isOpen(value)}
        onOpenChange={(next) => ctx.setItemOpen(value, next)}
        disabled={ctx.disabled || disabled}
        className={cn('border-b', className)}
        {...props}
      />
    </AccordionItemContext.Provider>
  );
});

const AccordionTrigger = React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'>>(function AccordionTrigger(
  { className, children, ...props },
  ref,
) {
  const { triggerId } = useAccordionItemContext('AccordionTrigger');
  return (
    <h3 className="flex">
      <CollapsibleTrigger
        ref={ref}
        {...{ [TRIGGER_ATTR]: '' }}
        className={cn(
          'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
        // After the spread: the content's `aria-labelledby` points at this id.
        id={triggerId}
      >
        {children}
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </CollapsibleTrigger>
    </h3>
  );
});

const AccordionContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(function AccordionContent(
  { className, children, style, ...props },
  ref,
) {
  const { triggerId } = useAccordionItemContext('AccordionContent');
  return (
    <CollapsibleContent
      ref={ref}
      role="region"
      aria-labelledby={triggerId}
      className="overflow-hidden text-sm transition-all data-[state=open]:animate-accordion-down motion-reduce:animate-none"
      style={{ '--radix-accordion-content-height': 'var(--radix-collapsible-content-height)', ...style } as React.CSSProperties}
      {...props}
    >
      <div className={cn('pb-4 pt-0', className)}>{children}</div>
    </CollapsibleContent>
  );
});

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
